// Opret state fra bane, sværhed og seed. Hash og resumé.

import { mapNode } from '../content/map';
import { getLevel } from '../content/levels';
import { newCity, startGraceTicks, newDatacenter, updateDemand } from './cities';
import { addCrew } from './crews';
import { difficulty, levelRampOf } from './difficulty';
import { DEFAULT_FEE, emptyPerks, startBudget } from './economy';
import { planYear } from './events';
import { dunkelKeepPm, dunkelStrength, heatKeepPm, heatStrength, trippedPlant } from './effects';
import { computeFlow, markDirty } from './flow';
import { dist, updateShields } from './grid';
import { newHz } from './hz';
import { makeStreams, nextInt } from './rng';
import type { CityTier, DailyMod, DifficultyId, GridNode, LevelDef, Line, ProducerState, SimEvent, State } from './types';
import { TICK_HZ, YEAR_TICKS } from './types';
import { makeWeather, seasonOf, sunPm, windPm } from './weather';

export const DEFAULT_CAP: Record<string, number> = { wind: 250, solar: 200, offshore: 1000, plant: 600 };

export interface CreateOptions {
  coop?: boolean;
  /** Makkerspil med faste roller (kun rum med to mennesker; IGN-løkke runde 4). */
  roles?: boolean;
  /** Fælles alarmer (kun rum med to mennesker; spor "coop"). */
  duo?: boolean;
  /** Dagens vagt: én modifikator for hele banen. */
  mod?: DailyMod;
}

function producer(capMw: number, fee: number, green: number): ProducerState {
  return { capMw, fee, feePaid: fee === 0, green, availMw: 0, usedMw: 0, boostMw: 0, boostTicks: 0 };
}

export function levelOf(state: State): LevelDef {
  return getLevel(state.levelId);
}

/** Opret en ny bane. `level` er en LevelDef eller et bane-id. Samme input giver altid samme state. */
/**
 * Banens fulde antal hjerter: sværhedens, plus ét i makkerspil (createState). Spor: tillid (co-op
 * runde 4): HUD'et tegnede sværhedens tre, mens rummet havde fire, så et tabt hjerte ikke kunne ses.
 */
export function trustMax(state: Pick<State, 'difficulty' | 'coop'>): number {
  return difficulty(state.difficulty).trust + (state.coop ? 1 : 0);
}

export function createState(level: LevelDef | string, diff: DifficultyId, seed: number, opts: CreateOptions = {}): State {
  const def = typeof level === 'string' ? getLevel(level) : level;
  const d = difficulty(diff);
  const rng = makeStreams(seed >>> 0);
  const nodes: GridNode[] = [];
  const pool: string[] = [];
  const rushPool: string[] = [];
  const pendingSites: { node: string; at: number }[] = [];

  const addNode = (id: string, init: (n: GridNode) => void): GridNode => {
    const m = mapNode(id);
    const n: GridNode = {
      id,
      name: m.name,
      kind: m.foreign ? 'foreign' : 'station',
      x: m.x,
      y: m.y,
      area: m.area,
      coast: m.coast,
      active: true,
      activeSince: 0,
      station: 0,
      phantom: false,
      flywheels: 0,
      shield: 0,
    };
    init(n);
    nodes.push(n);
    return n;
  };

  for (const site of def.sites) {
    addNode(site.node, (n) => {
      n.kind = site.kind;
      if (site.name) n.name = site.name;
      if (site.kind === 'city') {
        const tier = (site.tier ?? 0) as CityTier;
        // Fun pass: alle byer må vokse ét trin mere end banen siger (loft: storby med 12 huse).
        const maxTier = Math.min(4, (site.maxTier ?? tier + 2) + 1) as CityTier;
        n.city = newCity(tier, maxTier, site.baseMw);
      } else if (site.kind === 'datacenter') {
        n.city = newDatacenter(site.baseMw);
      } else if (site.kind === 'wind' || site.kind === 'solar' || site.kind === 'offshore' || site.kind === 'plant') {
        const cap = site.capMw ?? DEFAULT_CAP[site.kind] ?? 250;
        const fee = site.fee ?? DEFAULT_FEE[site.kind] ?? 0;
        n.producer = producer(cap, fee, site.green ?? (site.kind === 'plant' ? 0 : 1));
      }
      if (site.at === 'pool') {
        n.active = false;
        pool.push(site.node);
      } else if (site.at === 'rush') {
        n.active = false;
        rushPool.push(site.node);
      } else if (site.at !== undefined) {
        n.active = false;
        const at = typeof site.at === 'number' ? Math.round(site.at * TICK_HZ) : nextInt(rng.customers, Math.round(site.at[0] * TICK_HZ), Math.round(site.at[1] * TICK_HZ));
        pendingSites.push({ node: site.node, at });
      }
    });
  }

  // Byerne, der står der fra start, får samme pusterum som nye byer. Playtest runde 2: på Orkan bane 2
  // løb Vardes ur fra første sekund, før spilleren kunne nå at handle.
  for (const n of nodes) if (n.active && n.city) n.city.graceTicks = startGraceTicks(d);

  // Faste ledninger: udlandsnoder tilføjes efter behov.
  const lines: Line[] = [];
  let nextLineId = 1;
  for (const fl of def.lines) {
    for (const id of [fl.a, fl.b]) {
      if (!nodes.some((n) => n.id === id)) {
        addNode(id, (n) => {
          const m = mapNode(id);
          if (m.foreign) {
            n.kind = 'foreign';
            n.producer = producer(0, 0, m.green ?? 0.5);
          } else {
            n.kind = 'station';
          }
        });
      }
    }
    const a = nodes.find((n) => n.id === fl.a) as GridNode;
    const b = nodes.find((n) => n.id === fl.b) as GridNode;
    lines.push({
      id: `L${nextLineId++}`,
      a: fl.a,
      b: fl.b,
      km: Math.round(dist(a, b) * 10) / 10,
      medium: fl.medium,
      tier: fl.tier ?? 1,
      fixed: fl.fixed ?? (a.kind === 'foreign' || b.kind === 'foreign' || fl.medium === 'hvdc'),
      fixedCapMw: fl.capMw ?? 0,
      status: 'ok',
      halfDown: false,
      reinforced: false,
      flow: 0,
      loadPm: 0,
      overTicks: 0,
      congested: false,
      builtTick: -1000,
      spent: 0,
      owner: 'level',
    });
  }
  // Udlandsnoders produktion = summen af deres kabler.
  for (const n of nodes) {
    if (n.kind !== 'foreign' || !n.producer) continue;
    let cap = 0;
    for (const l of lines) if (l.a === n.id || l.b === n.id) cap += l.fixedCapMw;
    n.producer.capMw = cap;
  }
  pendingSites.sort((x, y) => x.at - y.at || (x.node < y.node ? -1 : 1));

  const pendingGrowth = def.growth
    .map((g) => ({
      node: g.node,
      at: typeof g.at === 'number' ? Math.round(g.at * TICK_HZ) : nextInt(rng.customers, Math.round(g.at[0] * TICK_HZ), Math.round(g.at[1] * TICK_HZ)),
      tier: g.tier,
    }))
    .sort((x, y) => x.at - y.at);
  const pendingBoosts = def.boosts.map((b) => ({ node: b.node, at: Math.round(b.at * TICK_HZ), mw: b.mw, ticks: Math.round(b.seconds * TICK_HZ) }));

  const seasons = (def.years + 2) * 4;
  const state: State = {
    v: 1,
    levelId: def.id,
    difficulty: diff,
    coop: opts.coop ?? false,
    ...(opts.roles ? { roles: true, hz: newHz() } : {}),
    ...(opts.duo ? { duo: true as const } : {}),
    seed: seed >>> 0,
    tick: 0,
    clockTick: 0,
    year: 0,
    phase: 'playing',
    phaseTicks: 0,
    finale: false,
    ...(opts.mod ? { mod: opts.mod } : {}),
    // Dagens vagt "Stram kasse": 30 % mindre at starte med.
    budget: Math.round(((startBudget(d.startBudgetFactor / Math.min(1, levelRampOf(d, def.number))) + (def.startKr ?? 0)) * (opts.mod === 'tight' ? 7 : 10)) / 10),
    // Makkerspil: ét hjerte ekstra, fordi to hænder også betyder to slags fejl (playtest runde 3).
    trust: d.trust + (opts.coop ? 1 : 0),
    nodes,
    lines,
    crews: [],
    threats: [],
    director: { planned: [], history: [], spent10: 0, budget10: 0, calmTicks: 0, nextId: 1, fronts: [], next: null, smaaLeftAt: -1000 },
    weather: makeWeather(rng.weather, seasons),
    flow: { lastTick: -1000, dirty: true, demandMw: 0, servedMw: 0, greenMw: 0, exportMw: 0, emergencyUntil: { DK1: -1, DK2: -1 } },
    yearStats: { servedSum: 0, greenSum: 0, exportSum: 0, ticks: 0, blackouts: 0, grownMw: 0, grant: 0 },
    stats: { breaksRepaired: 0, linesBuilt: 0, blackouts: 0, threatsFaced: [], greenRing: new Array<number>(60).fill(0), totalRing: new Array<number>(60).fill(0), greenSum: 0, servedSum: 0 },
    perks: emptyPerks(),
    packOffer: [],
    lastOffer: [],
    pool,
    customerPlan: customerSchedule(def, diff, pool.length),
    nextCustomerAt: 0,
    nextGrowthAt: d.growthEveryS * TICK_HZ,
    pendingGrowth,
    pendingSites,
    pendingBoosts,
    rng,
    lastSeq: { p1: 0, p2: 0 },
    nextLineId,
    nextCrewId: 1,
    nextThreatId: 1,
    result: null,
    lastIncome: 0,
    advancePaid: 0,
    cashCarryMilli: 0,
    earlyBonusPct: 0,
    lastBlackout: null,
    rushPool,
    power: {
      fcr: { readyAt: 0, until: 0, target: '', used: 0 },
      shed: { readyAt: 0, until: 0, target: '', used: 0 },
      norway: { readyAt: 0, until: 0, target: '', used: 0 },
    },
  };

  state.nextCustomerAt = state.customerPlan[0] ?? Number.MAX_SAFE_INTEGER;
  const sink: SimEvent[] = [];
  for (let i = 0; i < def.startCrews; i++) addCrew(state, 'van', def.crewHome, sink);
  if (def.shipHome) addCrew(state, 'ship', def.shipHome, sink);

  planYear(state, def, 0, false);
  updateAvailability(state);
  updateDemand(state, def);
  updateShields(state);
  computeFlow(state);
  return state;
}

/** Ticks mellem de tre første kunder fra puljen. */
export function customerInterval(diff: DifficultyId): number {
  return Math.round((60 * TICK_HZ) / Math.max(1, difficulty(diff).customersPerMin));
}

/** Antal kunder, der kommer i sværhedens faste tempo, før resten spredes ud over banen. */
export const EARLY_CUSTOMERS = 3;
/** Den sidste kunde fra puljen dukker op ved denne andel af banens længde. */
export const LAST_CUSTOMER_FRAC = 0.85;

/**
 * Hvornår hver af puljens `count` kunder dukker op (clockTick, stigende). De tre første kommer med
 * `customerInterval`, resten spredes jævnt fra den tredje til 85 % af banen, så kortet vokser til
 * finalen.
 */
export function customerSchedule(def: LevelDef, diff: DifficultyId, count: number): number[] {
  const every = customerInterval(diff);
  const end = Math.floor(LAST_CUSTOMER_FRAC * (def.years * YEAR_TICKS + def.finaleS * TICK_HZ));
  const out: number[] = [];
  const early = Math.min(EARLY_CUSTOMERS, count);
  for (let k = 0; k < early; k++) out.push((k + 1) * every);
  const rest = count - early;
  const t3 = early * every;
  for (let j = 0; j < rest; j++) out.push(Math.max(t3 + 1, t3 + Math.floor(((j + 1) * Math.max(0, end - t3)) / rest)));
  return out;
}

/** Producenternes tilgængelige effekt efter vejret. Markerer flowet beskidt ved ændring. */
export function updateAvailability(state: State): void {
  const heat = heatStrength(state);
  // Hedebølge: højtryk og vindstille, og varme solceller giver mindre. Havvinden løjer mindre af end
  // landvinden, og den milde (styrke 1) tager mindre, for på bane 1 er al strøm vind (spor "sværhed").
  const keep = heatKeepPm(heat);
  // Vindstille (bane 5): mørkt og stille, så både vind, havvind og sol falder. Den tager også
  // havvindens faste bund, for en dunkelflaute er netop dagene, hvor havet heller ikke blæser.
  const dk = dunkelKeepPm(dunkelStrength(state));
  // Dagens vagt "Stille dag": 20 % mindre vind hele banen.
  const modPm = state.mod === 'still' ? 800 : 1000;
  const wp = Math.round((windPm(state) * modPm) / 1000);
  const wind = Math.round((wp * Math.min(keep.wind, dk.wind)) / 1000);
  const sun = Math.round((sunPm(state) * Math.min(keep.sun, dk.sun)) / 1000);
  const offWind = Math.round((Math.min(1000, Math.round((wp * keep.sea) / 1000) + 150) * dk.sea) / 1000);
  let changed = false;
  for (const n of state.nodes) {
    const p = n.producer;
    if (!p) continue;
    let mw = 0;
    if (n.active && !trippedPlant(state, n.id)) {
      switch (n.kind) {
        case 'wind':
          mw = (p.capMw * wind) / 1000;
          break;
        case 'solar':
          mw = (p.capMw * sun) / 1000;
          break;
        case 'offshore':
          mw = (p.capMw * offWind) / 1000;
          break;
        default:
          mw = p.capMw;
      }
      if (p.boostTicks > 0) mw = Math.max(mw, p.boostMw);
    }
    mw = Math.round(mw);
    if (mw !== p.availMw) {
      p.availMw = mw;
      changed = true;
    }
  }
  if (changed) markDirty(state);
}

// --- Hash, klon og resumé ---------------------------------------------------------------------------

/**
 * Kanonisk serialisering: nøgler sorteres, så rækkefølgen felter blev oprettet i aldrig ændrer hashen.
 * Udefinerede felter udelades (som i JSON), og ikke-endelige tal skrives som null.
 */
export function canonical(v: unknown): string {
  if (v === null || typeof v !== 'object') {
    if (typeof v === 'number') return Number.isFinite(v) ? String(v) : 'null';
    return v === undefined ? 'null' : JSON.stringify(v);
  }
  if (Array.isArray(v)) return `[${v.map((x) => canonical(x)).join(',')}]`;
  const o = v as Record<string, unknown>;
  const keys = Object.keys(o)
    .filter((k) => o[k] !== undefined)
    .sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonical(o[k])}`).join(',')}}`;
}

/** FNV-1a (32 bit) over den kanoniske serialisering af hele state. */
export function hash(state: State): string {
  const s = canonical(state);
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

export function cloneState(state: State): State {
  return JSON.parse(JSON.stringify(state)) as State;
}

/** Leveres der noget som helst lige nu (eller i det sidste minut)? Ellers er grøn andel meningsløs. */
export function hasDelivery(state: State): boolean {
  for (let i = 0; i < 60; i++) if ((state.stats.totalRing[i] ?? 0) > 0) return true;
  return false;
}

/**
 * Grøn andel af det leverede forbrug over hele banen indtil nu. Målt over hele banen og ikke kun det
 * sidste år, så et stille vindår ikke alene afgør stjernen: det tæller, hvor tidligt man fik møllerne
 * på, og hvor lidt man lænede sig på kul undervejs.
 */
export function greenPct(state: State): number {
  const t = state.stats.servedSum;
  return t > 0 ? Math.round((state.stats.greenSum * 1000) / t) / 10 : 0;
}

export interface Summary {
  level: string;
  difficulty: DifficultyId;
  tick: number;
  year: number;
  season: number;
  phase: State['phase'];
  finale: boolean;
  budget: number;
  trust: number;
  greenPct: number;
  demandMw: number;
  servedMw: number;
  cities: { id: string; name: string; demandMw: number; servedMw: number; clockPm: number; out: boolean; shield: number }[];
  /** Alle aktive noder med type og (for producenter) hvad de kan og giver lige nu. */
  nodes: { id: string; name: string; kind: string; x: number; y: number; availMw: number; usedMw: number; linked: boolean }[];
  /** Alle ledninger med belastning i promille. */
  lineList: { id: string; a: string; b: string; medium: string; tier: number; status: string; loadPm: number; halfDown: boolean }[];
  lines: number;
  broken: number;
  crews: { id: string; task: string }[];
  threats: { id: string; kind: string; phase: string; inS: number; atRisk: number }[];
  result: State['result'];
}

/** Kompakt JSON-resumé til UI, test og agent. */
export function summary(state: State): Summary {
  const full = difficulty(state.difficulty).clockS * TICK_HZ * 100;
  return {
    level: state.levelId,
    difficulty: state.difficulty,
    tick: state.tick,
    year: state.year,
    season: seasonOf(state.clockTick),
    phase: state.phase,
    finale: state.finale,
    budget: state.budget,
    trust: state.trust,
    greenPct: greenPct(state),
    demandMw: state.flow.demandMw,
    servedMw: state.flow.servedMw,
    cities: state.nodes
      .filter((n) => n.active && n.city && !n.phantom)
      .map((n) => ({
        id: n.id,
        name: n.name,
        demandMw: n.city?.demandMw ?? 0,
        servedMw: n.city?.servedMw ?? 0,
        clockPm: Math.round(((n.city?.clock ?? 0) * 1000) / full),
        out: n.city?.out ?? false,
        shield: n.shield,
      })),
    nodes: state.nodes
      .filter((n) => n.active && !n.phantom)
      .map((n) => ({
        id: n.id,
        name: n.name,
        kind: n.kind,
        x: n.x,
        y: n.y,
        availMw: n.producer?.availMw ?? 0,
        usedMw: n.producer?.usedMw ?? 0,
        linked: state.lines.some((l) => l.status === 'ok' && (l.a === n.id || l.b === n.id)),
      })),
    lineList: state.lines.map((l) => ({ id: l.id, a: l.a, b: l.b, medium: l.medium, tier: l.tier, status: l.status, loadPm: l.loadPm, halfDown: l.halfDown })),
    lines: state.lines.length,
    broken: state.lines.filter((l) => l.status === 'broken').length,
    crews: state.crews.map((c) => ({ id: c.id, task: c.task })),
    threats: state.threats.map((t) => ({ id: t.id, kind: t.kind, phase: t.phase, inS: Math.max(0, Math.round((t.hitAt - state.clockTick) / TICK_HZ)), atRisk: t.atRisk.length })),
    result: state.result,
  };
}
